import { collectDocImageFileIds, isDocEmpty, tiptapDocToPlainText } from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import type {
    CommunicationChannel,
    CommunicationEntry,
    CreateEntryInput,
    EntryDirection,
    UpdateEntryInput
} from '../../../../services/communications';
import { channelDef } from './channels';
import { toDatetimeLocalValue } from './communicationFormatting';

const DURATION_ERROR = 'Duration must be a whole number of minutes, at least 1.';
const EMPTY_DOC: TiptapDoc = { type: 'doc', content: [] };

// Shared by the create page's entry section and the thread detail page's
// add/edit entry composer - one shape for logging a fresh entry or editing
// an existing one, channel-conditional the same way in both places.
export interface EntryFormValues {
    channel: CommunicationChannel;
    /** Optional headline for this interaction - '' means "none". Only ever surfaced on the detail page's composer, not the create page. */
    titleDisplay: string;
    /** Ignored (never sent) when channel === 'note'. */
    direction: EntryDirection;
    contactDisplay: string;
    contactNameDisplay: string;
    occurredAtLocal: string;
    /** Raw input text - '' means "not recorded". Only meaningful for channel 'call'. */
    durationMinutesDisplay: string;
    doc: TiptapDoc;
}

function docFromPlainText(text: string): TiptapDoc {
    return {
        type: 'doc',
        content: text
            .split('\n')
            .filter(Boolean)
            .map((line) => ({ type: 'paragraph', content: [{ type: 'text', text: line }] }))
    };
}

export function emptyEntryFormValues(
    channel: CommunicationChannel = 'call',
    defaultContact: string | null = null
): EntryFormValues {
    return {
        channel,
        titleDisplay: '',
        direction: 'incoming',
        contactDisplay: defaultContact ?? '',
        contactNameDisplay: '',
        occurredAtLocal: toDatetimeLocalValue(new Date()),
        durationMinutesDisplay: '',
        doc: EMPTY_DOC
    };
}

export function entryFormValuesFromEntry(entry: CommunicationEntry): EntryFormValues {
    return {
        channel: entry.channel,
        titleDisplay: entry.title ?? '',
        direction: entry.direction ?? 'incoming',
        contactDisplay: entry.contact ?? '',
        contactNameDisplay: entry.contactName ?? '',
        occurredAtLocal: toDatetimeLocalValue(new Date(entry.occurredAt)),
        durationMinutesDisplay: entry.durationMinutes != null ? String(entry.durationMinutes) : '',
        doc: entry.bodyDoc ?? (entry.body ? docFromPlainText(entry.body) : EMPTY_DOC)
    };
}

// Semantic, not structural: once the RichEditor round-trips a doc, tiptap's
// `getJSON()` output isn't guaranteed byte-identical to the original even
// with no meaningful edit (ProseMirror normalizes on parse), so a raw
// JSON.stringify diff would false-positive "changed". Text + image-id
// comparison is immune to that churn.
function docsEqual(a: TiptapDoc, b: TiptapDoc): boolean {
    if (isDocEmpty(a) && isDocEmpty(b)) return true;
    if (tiptapDocToPlainText(a) !== tiptapDocToPlainText(b)) return false;
    const imagesA = [...collectDocImageFileIds(a)].sort();
    const imagesB = [...collectDocImageFileIds(b)].sort();
    return imagesA.length === imagesB.length && imagesA.every((id, i) => id === imagesB[i]);
}

/** A well-formed whole-minute duration (>= 1), or undefined for empty/invalid input. */
function parseDurationMinutes(display: string): number | undefined {
    const trimmed = display.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
}

/** Only meaningful while the active channel is 'call' - callers gate the field's visibility on the same channelDef check. */
export function durationErrorFor(display: string): string | null {
    const trimmed = display.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 1 ? null : DURATION_ERROR;
}

export function entryFormValuesDiffer(a: EntryFormValues, b: EntryFormValues): boolean {
    return (
        a.channel !== b.channel ||
        a.titleDisplay.trim() !== b.titleDisplay.trim() ||
        a.direction !== b.direction ||
        a.contactDisplay.trim() !== b.contactDisplay.trim() ||
        a.contactNameDisplay.trim() !== b.contactNameDisplay.trim() ||
        a.occurredAtLocal !== b.occurredAtLocal ||
        a.durationMinutesDisplay.trim() !== b.durationMinutesDisplay.trim() ||
        !docsEqual(a.doc, b.doc)
    );
}

// Mirrors resolveEntryContent (backend): bodyDoc is only sent once docsEqual
// says the doc changed, and a sent-but-textless doc (image-only, or edited
// down to nothing) is what the server 400s on.
export function entryBodyWouldFailValidation(
    initialDoc: TiptapDoc,
    currentDoc: TiptapDoc
): boolean {
    if (docsEqual(initialDoc, currentDoc)) return false;
    return tiptapDocToPlainText(currentDoc).trim().length === 0;
}

/** Full create payload - channel-conditional fields (direction/contact/contactName/duration) are only ever included when that channel shows them. `title` isn't channel-conditional - it's optional on every channel. */
export function buildCreateEntryInput(values: EntryFormValues): CreateEntryInput {
    const def = channelDef(values.channel);
    const title = values.titleDisplay.trim();
    const contact = values.contactDisplay.trim();
    const contactName = values.contactNameDisplay.trim();
    const durationMinutes = def.hasDuration
        ? parseDurationMinutes(values.durationMinutesDisplay)
        : undefined;
    const hasBody = !isDocEmpty(values.doc);
    return {
        channel: values.channel,
        occurredAt: new Date(values.occurredAtLocal).toISOString(),
        ...(title && { title }),
        ...(def.hasDirection && { direction: values.direction }),
        ...(hasBody && { bodyDoc: values.doc }),
        ...(def.contactLabel && contact && { contact }),
        ...(def.contactNameLabel && contactName && { contactName }),
        ...(durationMinutes !== undefined && { durationMinutes })
    };
}

/** Only the fields that changed from `initial` (PATCH contract). A field a
 *  channel switch just hid is cleared with `null`, not dropped. */
export function buildUpdateEntryInput(
    initial: EntryFormValues,
    current: EntryFormValues
): UpdateEntryInput {
    const patch: UpdateEntryInput = {};
    const def = channelDef(current.channel);
    // `direction` falls back to 'incoming' even for a directionless 'note'
    // row, so "did it really have a direction" reads off the channel def.
    const initialDef = channelDef(initial.channel);

    if (current.channel !== initial.channel) {
        patch.channel = current.channel;
    }
    const title = current.titleDisplay.trim();
    if (title !== initial.titleDisplay.trim()) {
        patch.title = title || null;
    }
    if (def.hasDirection) {
        // Also resend if the initial channel had no direction at all.
        if (!initialDef.hasDirection || current.direction !== initial.direction) {
            patch.direction = current.direction;
        }
    } else if (initialDef.hasDirection) {
        patch.direction = null;
    }
    if (current.occurredAtLocal !== initial.occurredAtLocal) {
        patch.occurredAt = new Date(current.occurredAtLocal).toISOString();
    }
    if (!docsEqual(current.doc, initial.doc)) {
        patch.bodyDoc = current.doc;
    }

    const contact = current.contactDisplay.trim();
    if (!def.contactLabel) {
        if (initial.contactDisplay.trim()) patch.contact = null;
    } else if (contact !== initial.contactDisplay.trim()) {
        patch.contact = contact || null;
    }

    const contactName = current.contactNameDisplay.trim();
    if (!def.contactNameLabel) {
        if (initial.contactNameDisplay.trim()) patch.contactName = null;
    } else if (contactName !== initial.contactNameDisplay.trim()) {
        patch.contactName = contactName || null;
    }

    if (!def.hasDuration) {
        if (initial.durationMinutesDisplay.trim()) patch.durationMinutes = null;
    } else if (current.durationMinutesDisplay.trim() !== initial.durationMinutesDisplay.trim()) {
        patch.durationMinutes = parseDurationMinutes(current.durationMinutesDisplay) ?? null;
    }

    return patch;
}
